import React, { useState } from "react";
import { BASE_URL } from "../apiConfig";
import { Alert } from "./ui/alert";
import { tenantHeaders } from "../lib/tenantConfig";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";

interface ManageUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message?: string) => void;
}

const ROLE_OPTIONS = [
  { value: "user", label: "General User" },
  { value: "viewer", label: "Viewer" },
  { value: "ma", label: "M&A Team" },
  { value: "finance", label: "Finance Team" },
  { value: "strategy", label: "Strategy Team" },
  { value: "corporate_admin", label: "Corporate Admin" },
  { value: "staff", label: "Staff" },
  { value: "admin", label: "Admin" },
];

/** Generates a cryptographically random temp password that satisfies common password rules */
function generateTempPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => chars[b % chars.length])
    .join("");
}

const ManageUsersModal: React.FC<ManageUsersModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("user");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const validateForm = () => {
    if (!email.trim()) {
      setErrorMsg("Email address is required.");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Please enter a valid email address.");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return false;
    }
    return true;
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const adminToken = localStorage.getItem("authToken");
      if (!adminToken) throw new Error("Admin authentication required");

      const tempPassword = generateTempPassword();

      const response = await fetch(`${BASE_URL}/user/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: adminToken,
          ...tenantHeaders(),
        },
        body: JSON.stringify({ email, password: tempPassword, role: selectedRole }),
      });
      const data = await response.json();
      if (response.ok && data.status === "success") {
        // Send password reset email so the user can set their own password
        try {
          await sendPasswordResetEmail(auth, email);
        } catch {
          // Non-fatal — user was created, reset email is best-effort
        }
        setShowSuccessAlert(true);
        setTimeout(() => {
          setEmail("");
          setSelectedRole("user");
          onClose();
          onSuccess?.(`User added! Password setup email sent to ${email}`);
          setShowSuccessAlert(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 1800);
      } else {
        throw new Error(data.detail || "Failed to add user");
      }
    } catch (error: any) {
      setErrorMsg(error.message || "Failed to add user");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#FF541F] focus:ring-2 focus:ring-orange-100 transition-all text-sm font-medium";
  const labelCls = "block text-sm font-semibold text-slate-700 mb-1";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-xl p-6">

        {/* Header */}
        <div className="pb-4 border-b border-slate-100 mb-5">
          <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Sora, sans-serif" }}>
            Add New User
          </h3>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            A password setup link will be emailed to the user automatically.
          </p>
        </div>

        {/* Alerts */}
        <div className="absolute top-24 left-0 right-0 flex flex-col items-center gap-2 z-50 px-4">
          {showErrorAlert && (
            <Alert variant="destructive" close onClose={() => setShowErrorAlert(false)}>{errorMsg}</Alert>
          )}
          {showSuccessAlert && (
            <Alert variant="success" close onClose={() => setShowSuccessAlert(false)}>
              User added! Password setup email sent.
            </Alert>
          )}
        </div>

        <form onSubmit={handleAddUser} className="space-y-4">
          <div>
            <label className={labelCls}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className={inputCls}
              placeholder="user@company.com"
              disabled={loading}
            />
            <p className="text-xs text-slate-400 mt-1 font-medium">
              The user will receive an email to set their password.
            </p>
          </div>

          <div>
            <label className={labelCls}>Role / Department</label>
            <select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value)}
              className={inputCls}
              disabled={loading}
            >
              {ROLE_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-[#FF541F] hover:bg-[#e54a1a] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {loading ? "Adding User…" : "Add User & Send Email"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageUsersModal;
